import { Box } from '@mui/material';
import { useState } from 'react';
import { StandardJourneyFieldError, StandardJourneyInput } from '~/components/StandardJourney';
import {
  formatRenewalBirthdate,
  formatRenewalCpf,
  formatRenewalPhone,
  validateRenewalPersonalData,
  type RenewalPersonalFields
} from '../renewalReview';
import {
  RenewalReviewCard,
  RenewalReviewEditActions,
  RenewalReviewField,
  RenewalReviewLockNote
} from './RenewalReviewCard';
import { useRenewalCardSave } from './useRenewalCardSave';

export type RenewalPersonalData = RenewalPersonalFields & { birthdate: string; document: string };

type Props = {
  appId: string;
  data: RenewalPersonalData;
  editing: boolean;
  editDisabled: boolean;
  onEdit: () => void;
  onClose: () => void;
  onSave: (values: RenewalPersonalFields) => Promise<void>;
};

const toForm = (data: RenewalPersonalData): RenewalPersonalFields => ({
  name: data.name ?? '',
  mobile_phone: formatRenewalPhone(data.mobile_phone).replace('—', ''),
  email: data.email ?? ''
});

/** Card «Dados pessoais»: edita nome, celular e e-mail; nascimento e CPF são só leitura. */
export default function RenewalPersonalDataCard({
  appId,
  data,
  editing,
  editDisabled,
  onEdit,
  onClose,
  onSave
}: Props) {
  const [form, setForm] = useState<RenewalPersonalFields>(() => toForm(data));
  const [errors, setErrors] = useState<Partial<Record<keyof RenewalPersonalFields, string>>>({});
  const { saving, serverError, clearServerError, run } = useRenewalCardSave(
    'Não foi possível salvar seus dados. Revise as informações e tente novamente.'
  );

  const startEdit = () => {
    setForm(toForm(data));
    setErrors({});
    clearServerError();
    onEdit();
  };

  const cancel = () => {
    setForm(toForm(data));
    setErrors({});
    clearServerError();
    onClose();
  };

  const change = (field: keyof RenewalPersonalFields) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const save = async () => {
    const found = validateRenewalPersonalData(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (await run(() => onSave(form))) onClose();
  };

  return (
    <RenewalReviewCard
      appId={appId}
      editDisabled={editDisabled}
      editLabel="Editar dados pessoais"
      editing={editing}
      onEdit={startEdit}
      title="Dados pessoais"
    >
      {editing ? (
        <>
          <StandardJourneyInput
            appId={`${appId}-name`}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            label="Nome completo"
            onChange={change('name')}
            placeholder="Seu nome completo"
            value={form.name}
          />
          <StandardJourneyInput
            appId={`${appId}-phone`}
            error={Boolean(errors.mobile_phone)}
            errorMessage={errors.mobile_phone}
            label="Celular"
            mask="phone"
            onChange={change('mobile_phone')}
            placeholder="(DDD) 9 9999-9999"
            value={form.mobile_phone}
          />
          <StandardJourneyInput
            appId={`${appId}-email`}
            error={Boolean(errors.email)}
            errorMessage={errors.email}
            label="E-mail"
            onChange={change('email')}
            placeholder="Seu e-mail para cadastro de login"
            type="email"
            value={form.email}
          />
          <Box
            sx={(theme) => ({
              bgcolor: theme.customColors.backgroundMute,
              borderRadius: '8px',
              display: 'grid',
              gap: '12px',
              gridTemplateColumns: '1fr 1fr',
              p: '8px'
            })}
          >
            <RenewalReviewField label="Data de nascimento" value={formatRenewalBirthdate(data.birthdate)} />
            <RenewalReviewField label="CPF" value={formatRenewalCpf(data.document)} />
          </Box>
          <RenewalReviewLockNote divider message="Para alterar Data de nascimento e CPF, fale com o atendimento." />
          {serverError ? (
            <StandardJourneyFieldError
              appId={`${appId}-server-error`}
              id={`${appId}-server-error`}
              message={serverError}
            />
          ) : null}
          <RenewalReviewEditActions appId={appId} onCancel={cancel} onSave={save} saving={saving} />
        </>
      ) : (
        <>
          <RenewalReviewField label="Nome completo" value={data.name || '—'} />
          <RenewalReviewField label="Data de nascimento" value={formatRenewalBirthdate(data.birthdate)} />
          <RenewalReviewField label="CPF" value={formatRenewalCpf(data.document)} />
          <RenewalReviewField label="Celular" value={formatRenewalPhone(data.mobile_phone)} />
          <RenewalReviewField label="E-mail" value={data.email || '—'} />
        </>
      )}
    </RenewalReviewCard>
  );
}
